import {undef} from './undef';
import {toString} from './string';

function createItemAllowedKeysComparator(keys) {
    return function itemAllowedKeyComparator({key}) {
        return keys.includes(key);
    };
}

function itemEmptyValueComparator({value}) {
    return !value && value !== false && value !== 0;
}

function itemNoUndefValueComparator({value}) {
    return value !== undef;
}

function itemValueMapper({object, key}) {
    return object[key];
}

function itemEntryMapper({object, key}) {
    const value = itemValueMapper({object, key});

    return [key, value];
}

function createItemReplaceEntryTransformer(itemComparator, itemReplacer) {
    return function itemReplaceEntryTransformer({entry, key, value}) {
        if (itemComparator({key, value})) {
            return itemReplacer({key, value});
        }

        return entry;
    };
}

function createItemReplaceValueTransformer(value) {
    return function itemReplaceValueTransformer({key}) {
        return [key, value];
    };
}

function itemStringValueEntryTransformer({key, value}) {
    return [key, toString(value)];
}

function itemMergeResultReducer({result, key, value}) {
    return {
        ...result,
        [key]: value
    };
}

function getObjectKeys(object) {
    return Object.keys(object);
}

function getObjectItems(object, itemMapper) {
    return (
        getObjectKeys(object)
            .map((key, index, array) => (
                itemMapper({object, key, index, array})
            ))
    );
}

function getObjectValues(object) {
    return getObjectItems(object, itemValueMapper);
}

function getObjectEntries(object) {
    return getObjectItems(object, itemEntryMapper);
}

function getComparedObject(object, itemComparator, itemResultReducer) {
    return (
        getObjectEntries(object)
            .filter((entry, index, array) => {
                const [key, value] = entry;

                return itemComparator({entry, key, value, index, array});
            })
            .reduce((result, entry, index, array) => {
                const [key, value] = entry;

                return itemResultReducer({result, entry, key, value, index, array});
            }, {})
    );
}

function getTransformedObject(object, itemEntryTransformer, itemResultReducer) {
    return (
        getObjectEntries(object)
            .map((entry, index, array) => {
                const [key, value] = entry;

                return itemEntryTransformer({entry, key, value, index, array});
            })
            .reduce((result, entry, index, array) => {
                const [key, value] = entry;

                return itemResultReducer({result, entry, key, value, index, array});
            }, {})
    );
}

function getKeyedObject(object, keys) {
    return getComparedObject(object, createItemAllowedKeysComparator(keys), itemMergeResultReducer);
}

function getNoUndefValuesObject(object) {
    return getComparedObject(object, itemNoUndefValueComparator, itemMergeResultReducer);
}

function getDefaultValuesObject(object, defaultValue) {
    return getTransformedObject(object, createItemReplaceEntryTransformer(itemEmptyValueComparator, createItemReplaceValueTransformer(defaultValue)), itemMergeResultReducer);
}

function getStringValuesObject(object) {
    return getTransformedObject(object, itemStringValueEntryTransformer, itemMergeResultReducer);
}

export {
    itemNoUndefValueComparator,
    getObjectKeys,
    getObjectValues,
    getObjectEntries,
    getKeyedObject,
    getNoUndefValuesObject,
    getDefaultValuesObject,
    getStringValuesObject
};
